// Main entry point for translations with lazy loading support
import type { Language, TranslationKey, Translations } from './types';

// Re-export types for use throughout the application
export type { Language, TranslationKey, Translations };

// Language names for display in language selector
export const languageNames: Record<Language, string> = {
  en: 'English',
  cs: 'Čeština',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  pl: 'Polski',
  pt: 'Português',
  nl: 'Nederlands',
};

// Cache for loaded translations to avoid re-importing
const translationCache: Partial<Record<Language, Translations>> = {};

/**
 * Dynamically loads translations for a given language
 * @param language - The language code to load
 * @returns Promise resolving to the translations object
 */
export async function loadTranslations(language: Language): Promise<Translations> {
  // Return cached translations if already loaded
  if (translationCache[language]) {
    return translationCache[language]!;
  }

  // Dynamically import the language file
  let translations: Translations;

  switch (language) {
    case 'en':
      translations = (await import('./en')).default;
      break;
    case 'cs':
      translations = (await import('./cs')).default;
      break;
    case 'de':
      translations = (await import('./de')).default;
      break;
    case 'fr':
      translations = (await import('./fr')).default;
      break;
    case 'es':
      translations = (await import('./es')).default;
      break;
    case 'it':
      translations = (await import('./it')).default;
      break;
    case 'pl':
      translations = (await import('./pl')).default;
      break;
    case 'pt':
      translations = (await import('./pt')).default;
      break;
    case 'nl':
      translations = (await import('./nl')).default;
      break;
    default:
      // Fallback to English if language not found
      console.warn(`Language '${language}' not found, falling back to English`);
      translations = (await import('./en')).default;
  }

  // Cache the loaded translations
  translationCache[language] = translations;

  return translations;
}

/**
 * Preloads translations for a given language
 * Useful for preloading the default language or anticipated language switches
 * @param language - The language code to preload
 */
export function preloadTranslations(language: Language): void {
  loadTranslations(language).catch(err => {
    console.error(`Failed to preload translations for ${language}:`, err);
  });
}

/**
 * Clears the translation cache
 * Useful for development or testing
 */
export function clearTranslationCache(): void {
  Object.keys(translationCache).forEach(key => {
    delete translationCache[key as Language];
  });
}
